import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UploadSimple, FilePdf, FileXls, FileCsv, CheckCircle, Lock, Bank, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const KINDS = [
  { id: "petpooja_invoices", title: "PetPooja invoices",      desc: "vendor, store, item, qty, rate, amount, invoice_date", grad: "from-rose-500 to-pink-600",     iconBg: "bg-rose-100 text-rose-700" },
  { id: "tally",             title: "Tally ledger",           desc: "date, party, amount, txn_type, kind, store",            grad: "from-violet-500 to-purple-700", iconBg: "bg-violet-100 text-violet-700" },
  { id: "bank",              title: "Bank statement",         desc: "date, amount, txn_type, vendor_tag, description",       grad: "from-indigo-500 to-blue-700",   iconBg: "bg-indigo-100 text-indigo-700" },
  { id: "payouts",           title: "Swiggy / Zomato payouts",desc: "Swiggy CSV/ZIP, Zomato Settlement XLSX — or drop a Swiggy Partner Portal screenshot (PNG/JPG): captures BOTH the in-progress payout AND any pending past-payout rows in one shot",                  grad: "from-orange-500 to-amber-600",  iconBg: "bg-orange-100 text-orange-800" },
  { id: "sales",             title: "PetPooja sales",         desc: "CSV columns: date, store, source, amount — OR drop a screenshot of the PetPooja POS Sales Statistics dashboard (PNG/JPG) to capture outlet Cash/Card/Online split via Vision OCR", grad: "from-teal-500 to-emerald-600",  iconBg: "bg-teal-100 text-teal-700" },
];

export default function UploadsTab({ onUploaded }) {
  const [accounts, setAccounts] = useState([]);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await api.get("/accounts");
      setAccounts(res.data || []);
    } catch { setAccounts([]); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleUploaded = () => {
    loadAccounts();
    onUploaded?.();
  };

  const deleteAccount = async (acc) => {
    const txnHint = acc.txn_count ? ` This will also remove ${acc.txn_count} transaction${acc.txn_count === 1 ? "" : "s"}.` : "";
    if (!window.confirm(`Delete bank account "${acc.name}"?${txnHint} This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/accounts/${acc.id}`);
      toast.success(`Removed "${acc.name}" · ${r.data?.deleted_txns || 0} txns cleared`);
      await loadAccounts();
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete account failed");
    }
  };

  return (
    <div data-testid="uploads-tab" className="space-y-5">
      <div>
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm grid place-items-center bg-sky-100 text-sky-700"><UploadSimple size={15} weight="duotone" /></div>
          <h2 className="font-heading font-bold text-2xl tracking-tight">Data uploads</h2>
        </div>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">
          Drop <Tag c="rose">CSV</Tag> <Tag c="emerald">Excel</Tag> <Tag c="violet">PDF</Tag> files. Columns shown are the expected schema; extra columns are ignored. PDFs must contain tabular data with a header row.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {KINDS.map((k) => <UploadCard key={k.id} kind={k} accounts={accounts} onDone={handleUploaded} onDeleteAccount={deleteAccount} />)}
      </div>
    </div>
  );
}

function Tag({ c, children }) {
  const palette = {
    rose: "bg-rose-100 text-rose-700 border-rose-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
  }[c];
  return <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold mx-0.5 ${palette}`}>{children}</span>;
}

function UploadCard({ kind, onDone, accounts = [], onDeleteAccount }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [password, setPassword] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountMode, setAccountMode] = useState("select"); // 'select' or 'new'
  const [diag, setDiag] = useState(null);

  // Default to the first known account when accounts list loads
  useEffect(() => {
    if (kind.id === "bank" && !accountName && accounts.length > 0 && accountMode === "select") {
      setAccountName(accounts[0].name);
    }
  }, [accounts, kind.id, accountName, accountMode]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setBusy(true);
    let aggInserted = 0, aggDuplicate = 0, aggSkipped = 0;
    const perFile = [];
    let lastDiag = null;
    try {
      // Client-side pre-flight: if any file is a PDF that appears encrypted
      // AND no password has been entered, refuse the upload here. Saves the
      // user a Cloudflare round-trip (which occasionally 520s on cold origin
      // containers) and shows a clear, immediate message. The `/Encrypt`
      // marker lives in the PDF trailer, so we check the last 8KB (the head
      // rarely contains it for real-world statements).
      if (!password) {
        const encryptedFile = await (async () => {
          for (const f of files) {
            if (!/\.pdf$/i.test(f.name)) continue;
            try {
              const tailStart = Math.max(0, f.size - 8192);
              const tail = await f.slice(tailStart).arrayBuffer();
              const text = new TextDecoder("latin1").decode(tail);
              if (text.includes("/Encrypt")) return f.name;
            } catch { /* ignore, let the backend handle it */ }
          }
          return null;
        })();
        if (encryptedFile) {
          toast.error(`${kind.title}: "${encryptedFile}" is password-protected. Enter the PDF password above and try again.`, { duration: 10000 });
          setBusy(false);
          return;
        }
      }

      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        if (password) fd.append("password", password);
        if (accountName && kind.id === "bank") fd.append("account", accountName);

        // For bank uploads (which can take 30–90s and used to trip Cloudflare 520s),
        // use the two-phase queue + poll flow so the HTTP request returns in <500ms.
        const useAsync = kind.id === "bank";

        try {
          let res;
          if (useAsync) {
            const enq = await api.post(`/uploads/${kind.id}/queue`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            const jobId = enq?.data?.job_id;
            if (!jobId) throw new Error("Queue did not return job_id");

            // Poll: every 1.5s, up to 3 minutes total (bank statements are almost
            // always parsed in < 60s on Preview; give generous headroom for prod).
            const startedAt = Date.now();
            const MAX_MS = 3 * 60 * 1000;
            let job = null;
            while (Date.now() - startedAt < MAX_MS) {
              await new Promise((r) => setTimeout(r, 1500));
              const p = await api.get(`/uploads/jobs/${jobId}`);
              job = p.data;
              if (job.status === "done" || job.status === "error") break;
            }
            if (!job || (job.status !== "done" && job.status !== "error")) {
              throw new Error("Parse is taking longer than expected. Please refresh and check the diag panel in a minute.");
            }
            if (job.status === "error") {
              throw { response: { data: { detail: job.error } } };
            }
            res = { data: job.result || {} };
          } else {
            res = await api.post(`/uploads/${kind.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
          const isZip = res.data && Array.isArray(res.data.per_file);
          const inserted = isZip ? (res.data.total_inserted || 0) : (res.data.inserted || 0);
          const duplicate = isZip ? (res.data.total_duplicate || 0) : (res.data.duplicate || 0);
          const skipped = res.data.skipped || 0;
          aggInserted += inserted; aggDuplicate += duplicate; aggSkipped += skipped;
          perFile.push({ file: file.name, inserted, duplicate, skipped, ok: true });
          lastDiag = { ...res.data, file: file.name };
        } catch (e) {
          perFile.push({ file: file.name, ok: false, error: e?.response?.data?.detail || e.message });
        }
      }
      setDone((d) => d + aggInserted);
      setDiag({
        inserted: aggInserted, duplicate: aggDuplicate, skipped: aggSkipped,
        per_file: perFile, detected_columns: lastDiag?.detected_columns,
        sample_row: lastDiag?.sample_row, files_count: files.length,
      });
      const ok = perFile.filter((f) => f.ok).length;
      const failed = perFile.filter((f) => !f.ok).length;
      if (files.length > 1) {
        toast.success(
          `${kind.title}: ${ok}/${files.length} files ingested — ${aggInserted} new${aggDuplicate ? `, ${aggDuplicate} duplicate` : ""}${failed ? `, ${failed} failed` : ""}`,
          { duration: 6000 }
        );
      } else if (aggInserted > 0) {
        toast.success(`${kind.title}: ${aggInserted} new${aggDuplicate ? `, ${aggDuplicate} duplicate skipped` : ""}${aggSkipped ? `, ${aggSkipped} invalid` : ""}`);
      } else if (aggDuplicate > 0) {
        toast.info(`${kind.title}: all ${aggDuplicate} rows are duplicates — nothing new to import.`);
      } else if (failed > 0) {
        // Surface the actual detail (e.g. "PDF is password-protected. Enter…")
        // so the user knows exactly what to fix, especially when they upload
        // a single file and there's no per-file grid below.
        const firstErr = perFile.find((f) => !f.ok);
        if (files.length === 1 && firstErr?.error) {
          toast.error(`${kind.title}: ${firstErr.error}`, { duration: 10000 });
        } else {
          toast.error(`${kind.title}: ${failed} file(s) failed to import`);
        }
      } else {
        toast.error(`${kind.title}: 0 rows imported. Check detected columns below.`, { duration: 6000 });
      }
      setPassword("");
      onDone?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden shadow-sm hover:shadow transition-shadow" data-testid={`upload-card-${kind.id}`}>
      <div className={`h-1 bg-gradient-to-r ${kind.grad}`} />
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 grid place-items-center rounded-sm ${kind.iconBg}`}><UploadSimple size={18} weight="duotone" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-slate-900">{kind.title}</div>
            <div className="text-[11px] text-slate-500 font-mono mt-1 truncate" title={kind.desc}>{kind.desc}</div>
          </div>
          {done > 0 && (
            <div className="flex items-center gap-1 text-emerald-700 text-xs font-semibold whitespace-nowrap">
              <CheckCircle size={14} weight="fill" /> {done}
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-rose-50 text-rose-700 border border-rose-200"><FileCsv size={12} weight="duotone" /> CSV</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200"><FileXls size={12} weight="duotone" /> XLS</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-violet-50 text-violet-700 border border-violet-200"><FilePdf size={12} weight="duotone" /> PDF</span>
          {kind.id === "payouts" && (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-amber-50 text-amber-700 border border-amber-200">ZIP</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-cyan-50 text-cyan-700 border border-cyan-200">PNG / JPG</span>
            </>
          )}
          {kind.id === "sales" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-cyan-50 text-cyan-700 border border-cyan-200">PNG / JPG</span>
          )}
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
            <Lock size={11} weight="bold" /> PDF password
            <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Leave blank if not encrypted"
            className="mt-1 w-full h-8 px-2.5 text-xs rounded-sm border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 placeholder:text-slate-400"
            disabled={busy}
            data-testid={`upload-password-${kind.id}`}
            autoComplete="off"
          />
        </div>

        {kind.id === "bank" && (
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
              <Bank size={11} weight="bold" /> Account
              <span className="text-slate-400 font-normal normal-case tracking-normal">— which bank statement is this?</span>
            </label>
            {accountMode === "select" ? (
              <div className="mt-1 flex items-center gap-1.5">
                <Select
                  value={accountName}
                  onValueChange={(v) => {
                    if (v === "__new__") {
                      setAccountMode("new");
                      setAccountName("");
                    } else {
                      setAccountName(v);
                    }
                  }}
                  disabled={busy}
                >
                  <SelectTrigger className="h-8 px-2.5 text-xs rounded-sm border-slate-200 bg-slate-50 hover:bg-white flex-1" data-testid={`upload-account-${kind.id}`}>
                    <SelectValue placeholder={accounts.length === 0 ? "No accounts yet — add new" : "Select account"} />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <div key={a.id} className="flex items-center group">
                        <SelectItem value={a.name} className="flex-1" data-testid={`upload-account-option-${a.id}`}>
                          <div className="flex items-center gap-2">
                            <Bank size={11} weight="duotone" className="text-indigo-600" />
                            {a.name}
                          </div>
                        </SelectItem>
                        {onDeleteAccount && (
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onDeleteAccount(a);
                              if (accountName === a.name) setAccountName("");
                            }}
                            className="opacity-0 group-hover:opacity-100 h-7 w-7 grid place-items-center text-slate-500 hover:text-rose-500 hover:bg-rose-50 rounded-sm transition-opacity"
                            title={`Remove ${a.name}`}
                            data-testid={`upload-account-delete-${a.id}`}
                          >
                            <Trash size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    ))}
                    <SelectItem value="__new__" data-testid="upload-account-add-new">
                      <div className="flex items-center gap-2 text-indigo-600 font-semibold">
                        <Plus size={11} weight="bold" />
                        Add new account…
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. Axis FD, Kotak Savings"
                  className="flex-1 h-8 px-2.5 text-xs rounded-sm border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 placeholder:text-slate-400"
                  disabled={busy}
                  data-testid={`upload-account-new-${kind.id}`}
                  autoComplete="off"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => { setAccountMode("select"); setAccountName(accounts[0]?.name || ""); }}
                  className="h-8 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm"
                  data-testid={`upload-account-cancel-${kind.id}`}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        <label className="mt-4 block">
          <input
            type="file"
            multiple
            accept={
              kind.id === "payouts"
                ? ".csv,.xlsx,.xls,.zip,.png,.jpg,.jpeg,.webp,application/zip,application/x-zip-compressed,application/octet-stream,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,image/png,image/jpeg,image/webp"
                : kind.id === "sales"
                ? ".csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,image/png,image/jpeg,image/webp"
                : ".csv,.xlsx,.xls,.pdf,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            }
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid={`upload-input-${kind.id}`}
            disabled={busy}
          />
          <Button asChild variant="default" size="sm" className={`w-full h-9 rounded-sm bg-gradient-to-r ${kind.grad} hover:opacity-95 text-white cursor-pointer shadow-sm`}>
            <span>
              <UploadSimple size={14} weight="bold" className="mr-1.5" />
              {busy ? "Uploading…" : "Choose file(s)"}
            </span>
          </Button>
        </label>

        {diag && (
          <div className={`mt-3 rounded-sm border text-[11px] p-2.5 ${diag.auto_routed ? "bg-cyan-50 border-cyan-200 text-cyan-900" : (diag.inserted > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : (diag.per_file?.some((f) => !f.ok) ? "bg-rose-50 border-rose-200 text-rose-900" : (diag.duplicate > 0 ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-amber-50 border-amber-200 text-amber-900")))}`} data-testid={`upload-diag-${kind.id}`}>
            <div className="font-bold uppercase tracking-wider text-[10px] mb-1.5 flex items-center justify-between">
              <span>{diag.auto_routed ? "Auto-routed to New PO approvals" : (diag.inserted > 0 ? "Import OK" : (diag.per_file?.some((f) => !f.ok) ? "Upload failed" : (diag.duplicate > 0 ? "All duplicates" : "Nothing imported")))}</span>
              <span className="font-mono">{diag.auto_routed ? "1↗" : `${diag.inserted}↑ ${diag.duplicate ? `${diag.duplicate}⇆ ` : ""}${diag.skipped}∅`}</span>
            </div>
            {diag.auto_routed && diag.new_po && (
              <div className="mt-1 space-y-1">
                <div className="text-[11px] leading-relaxed">{diag.message}</div>
                <div className="mt-2 p-2 rounded-sm bg-white/70 border border-cyan-300">
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">Parsed PO</div>
                  <div className="font-mono text-[11px] leading-tight">
                    <div><b>{diag.new_po.vendor || "—"}</b> · {diag.new_po.po_number || "—"}</div>
                    <div>{diag.new_po.po_date || "—"} · {diag.new_po.outlet || "—"} · ₹{diag.new_po.amount?.toLocaleString("en-IN") || "—"}</div>
                  </div>
                </div>
              </div>
            )}
            {!diag.auto_routed && diag.per_file && diag.per_file.length >= 1 && (diag.per_file.length > 1 || !diag.per_file[0].ok) && (
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Per-file</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {diag.per_file.map((f, i) => (
                    <div key={i} className={`flex items-start gap-2 font-mono text-[11px] ${f.ok ? "" : "text-rose-700"}`}>
                      <span className="shrink-0">{f.ok ? "✓" : "✗"}</span>
                      <span className="truncate min-w-0" title={f.file}>{f.file}</span>
                      <span className={f.ok ? "shrink-0" : "flex-1 break-words whitespace-normal"} title={f.error}>
                        {f.ok ? `${f.inserted}↑ ${f.duplicate ? `${f.duplicate}⇆` : ""}` : f.error}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {diag.detected_columns && (
              <div className="mt-1">
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">Detected columns</div>
                <div className="font-mono break-words">{diag.detected_columns.join(", ") || "—"}</div>
              </div>
            )}
            {diag.sample_row && (
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">First row sample</div>
                <pre className="font-mono whitespace-pre-wrap break-words leading-tight opacity-90">{JSON.stringify(diag.sample_row, null, 1).slice(0, 600)}</pre>
              </div>
            )}
            {diag.inserted === 0 && diag.detected_columns && !diag.per_file?.some((f) => !f.ok) && (
              <div className="mt-2 pt-2 border-t border-current/20 text-[11px]">
                Share these column names with the dev and they&apos;ll be added to the parser, or rename the headers in your file to match the schema above.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
